import { useCallback, useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { useAppStore } from '@core/store'
import { useJanelaAberta } from '@core/layout/useJanelaAberta'
import {
  applyFilterAsSelection,
  type FilterQuery,
} from '@viewer/lib/filter'
import { FilterQueryEditor } from '@viewer/filter/FilterQueryEditor'
import { newFilterRule } from '@viewer/filter/filterQuery'

// Floating "Filtro" panel.
//
// Builds a multi-criterion query over the loaded models and pushes the
// matching items as the viewer's selection. The user can then act on
// the selection with the existing topbar buttons (Esconder, Isolar,
// Aparência, etc.).
//
// Each rule = (dimension, operator, value). Rules are combined with
// AND or OR. Property dimension also takes a free-text key (e.g.
// "Pset_WallCommon.IsExternal" or "Name"). Numeric operators (>, <,
// etc.) only apply to the property dimension.
//
// O construtor de regras propriamente dito vive em
// `@viewer/filter/FilterQueryEditor`: é o MESMO que a detecção de
// conflitos usa para definir um conjunto por regra. Este painel ficou
// com o que é dele (janela, aplicar como selecção, contagem).

function FloatingFilterPanel() {
  const open = useAppStore((s) => s.filterPanelOpen)
  const setOpen = useAppStore((s) => s.setFilterPanelOpen)
  const { z, trazer } = useJanelaAberta('filtro', open)
  const models = useAppStore((s) => s.models)
  const filterPrefill = useAppStore((s) => s.filterPrefill)
  const setFilterPrefill = useAppStore((s) => s.setFilterPrefill)

  const [query, setQuery] = useState<FilterQuery>(() => ({
    combinator: 'and',
    rules: [newFilterRule()],
  }))

  // O dicionário de propriedades (Info do modelo) abre o Filtro já
  // apontado a um Pset.Propriedade via filterPrefill. Adotamos a query
  // DURANTE o render (padrão recomendado do React para "ajustar estado
  // quando um valor externo muda", sem efeito nem set-state-in-effect),
  // com guarda por referência para aplicar cada pedido uma só vez.
  const [lastPrefill, setLastPrefill] = useState<FilterQuery | null>(null)
  if (filterPrefill && filterPrefill !== lastPrefill) {
    setLastPrefill(filterPrefill)
    setQuery(filterPrefill)
  }
  // Limpar o canal do store depois de consumido (setter do store, não é
  // estado local) para não reaplicar a mesma query num reabrir manual.
  useEffect(() => {
    if (filterPrefill) setFilterPrefill(null)
  }, [filterPrefill, setFilterPrefill])
  const [busy, setBusy] = useState(false)
  const [matchCount, setMatchCount] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [position, setPosition] = useState({ x: 240, y: 260 })
  const dragRef = useRef<{
    pointerStart: { x: number; y: number }
    posStart: { x: number; y: number }
  } | null>(null)

  const onTitleDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      dragRef.current = {
        pointerStart: { x: e.clientX, y: e.clientY },
        posStart: { ...position },
      }
      ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
    },
    [position],
  )

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!dragRef.current) return
      const dx = e.clientX - dragRef.current.pointerStart.x
      const dy = e.clientY - dragRef.current.pointerStart.y
      setPosition({
        x: dragRef.current.posStart.x + dx,
        y: dragRef.current.posStart.y + dy,
      })
    }
    const onUp = () => {
      dragRef.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [])

  if (!open) return null
  if (models.length === 0) {
    return (
      <div
        onPointerDownCapture={trazer}
        className="fixed w-[360px] rounded border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-[11px] text-[var(--color-text-muted)] shadow-2xl"
        style={{ zIndex: z, left: position.x, top: position.y }}
      >
        Carrega um IFC para usar o filtro.
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="ml-2 rounded border border-[var(--color-border)] px-1.5 py-0 text-[10px]"
        >
          ×
        </button>
      </div>
    )
  }

  const onQueryChange = (next: FilterQuery) => {
    setQuery(next)
    setMatchCount(null)
  }

  const apply = async () => {
    setBusy(true)
    setError(null)
    try {
      const count = await applyFilterAsSelection(query)
      setMatchCount(count)
      if (count === 0) {
        setError(
          'Nenhum elemento corresponde a todos os critérios. Verifica os valores e operadores.',
        )
      }
    } catch (err) {
      console.warn('[filter] apply failed', err)
      setError('Erro a aplicar o filtro. Vê a consola.')
    } finally {
      setBusy(false)
    }
  }
  const clear = () => {
    useAppStore.getState().clearSelection()
    setMatchCount(null)
    setError(null)
  }

  return (
    <div
      onPointerDownCapture={trazer}
      className="fixed flex flex-col rounded border border-[var(--color-border)] bg-[var(--color-surface)] text-[11px] text-[var(--color-text)] shadow-2xl"
      style={{ zIndex: z, left: position.x, top: position.y, width: 460 }}
    >
      <div
        onPointerDown={onTitleDown}
        className="flex h-7 shrink-0 cursor-move items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-panel)] px-2 select-none"
      >
        <div className="flex items-center gap-2">
          <span className="font-medium text-[var(--color-text)]">Filtro</span>
          {matchCount !== null && (
            <span className="rounded bg-[var(--color-accent-bg)] px-1.5 py-0 text-[10px] text-[var(--color-accent)]">
              {matchCount} elemento{matchCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => setOpen(false)}
          className="rounded px-1.5 py-0 text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          title="Fechar"
        >
          <X size={12} />
        </button>
      </div>

      <div className="flex flex-col gap-2 p-3">
        <FilterQueryEditor query={query} onChange={onQueryChange} />

        {/* Action buttons */}
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={apply}
            disabled={busy}
            className="flex-1 rounded bg-[var(--color-accent)] px-2 py-1 font-medium text-[var(--color-bg)] hover:opacity-90 disabled:opacity-40"
          >
            {busy ? 'A aplicar…' : 'Aplicar'}
          </button>
          <button
            type="button"
            onClick={clear}
            disabled={busy}
            className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
          >
            Limpar selecção
          </button>
        </div>

        {error && (
          <div className="rounded bg-[var(--color-accent-bg)] px-2 py-1 text-[10px] text-[var(--color-accent)]">
            {error}
          </div>
        )}

        <div className="text-[10px] text-[var(--color-text-muted)]">
          O resultado aparece como selecção no viewer. Usa Esconder /
          Isolar / Aparência da topbar para agir sobre os elementos
          escolhidos.
        </div>
      </div>
    </div>
  )
}
export default FloatingFilterPanel
